/**
 * Plan definitions — PURE constants + helpers, safe to import from client
 * code (no prisma). Server-side enforcement lives in src/lib/plan.ts.
 */

export type PlanId = "free" | "starter" | "pro" | "scale" | "founder";

export type PlanLimits = {
  automations: number;
  maps: number;
  /** Companies a single user can own. */
  companiesPerUser: number;
  /** Seats per company (members + pending invites). */
  usersPerCompany: number;
  contacts: number;
  /** Step/content nodes inside one flow container (the trigger rides free). */
  nodesPerFlow: number;
  /** Map nodes living outside any flow (annotations, providers, pages...). */
  looseNodes: number;
};

const UNLIMITED = Number.POSITIVE_INFINITY;

export const PLAN_LIMITS: Record<PlanId, PlanLimits> = {
  free: {
    automations: 20,
    maps: 1,
    companiesPerUser: 1,
    usersPerCompany: 1,
    contacts: 500,
    nodesPerFlow: 10,
    looseNodes: 30,
  },
  starter: {
    automations: 50,
    maps: 5,
    companiesPerUser: 1,
    usersPerCompany: 3,
    contacts: 5_000,
    nodesPerFlow: 25,
    looseNodes: 150,
  },
  pro: {
    automations: UNLIMITED,
    maps: UNLIMITED,
    companiesPerUser: 3,
    usersPerCompany: 10,
    contacts: 25_000,
    nodesPerFlow: UNLIMITED,
    looseNodes: UNLIMITED,
  },
  scale: {
    automations: UNLIMITED,
    maps: UNLIMITED,
    companiesPerUser: UNLIMITED,
    usersPerCompany: UNLIMITED,
    contacts: 100_000,
    nodesPerFlow: UNLIMITED,
    looseNodes: UNLIMITED,
  },
  founder: {
    automations: UNLIMITED,
    maps: UNLIMITED,
    companiesPerUser: UNLIMITED,
    usersPerCompany: UNLIMITED,
    contacts: UNLIMITED,
    nodesPerFlow: UNLIMITED,
    looseNodes: UNLIMITED,
  },
};

export const PLAN_NAMES: Record<PlanId, string> = {
  free: "Free",
  starter: "Starter",
  pro: "Pro",
  scale: "Scale",
  founder: "Founder",
};

/* ---------- map node counting (shared by map save + mapper guards) ---------- */

export type MapNodeLike = {
  id: string;
  type?: string;
  parentNode?: string;
  data?: { kind?: string };
};

export type MapNodeViolation = {
  feature: "nodesPerFlow" | "looseNodes";
  limit: number;
  count: number;
};

/**
 * Checks a map's node set against plan limits. Containers (flows/folders)
 * and trigger nodes don't count; nodes parented to a flow count toward that
 * flow, everything else is "loose".
 */
export function mapNodeViolation(
  nodes: MapNodeLike[],
  limits: PlanLimits
): MapNodeViolation | null {
  const flowIds = new Set(
    nodes.filter((n) => n.type === "flow").map((n) => n.id)
  );
  const perFlow = new Map<string, number>();
  let loose = 0;
  for (const n of nodes) {
    if (n.type === "flow" || n.type === "folder") continue;
    if (n.data?.kind === "auto-trigger") continue;
    if (n.parentNode && flowIds.has(n.parentNode)) {
      perFlow.set(n.parentNode, (perFlow.get(n.parentNode) ?? 0) + 1);
    } else {
      loose += 1;
    }
  }
  for (const count of perFlow.values()) {
    if (count > limits.nodesPerFlow)
      return { feature: "nodesPerFlow", limit: limits.nodesPerFlow, count };
  }
  if (loose > limits.looseNodes)
    return { feature: "looseNodes", limit: limits.looseNodes, count: loose };
  return null;
}

/** Steps that count as nodes — synthetic branch-join gotos are wiring. */
export function countFlowSteps(steps: { id?: string }[]): number {
  return steps.filter((s) => !String(s.id ?? "").includes("-join:")).length;
}

export const LIMIT_NOUNS: Record<string, string> = {
  automations: "flows",
  maps: "maps",
  usersPerCompany: "users",
  companiesPerUser: "workspaces",
  contacts: "contacts",
  nodesPerFlow: "nodes in one flow",
  looseNodes: "nodes outside flows",
};
